import React, { useCallback, useEffect, useRef, useState } from 'react';
import IProcedureActionOverlayDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionOverlayDetail';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import DraggableSliderContainer from '../DraggableSlider/DraggableSliderContainer';

type Props = {
  imageDataUrl: string;
  overlays: IProcedureActionOverlayDetail[];
  onImageClick?: () => void;
  enableWheelZoom?: boolean;
  hideZoomSlider?: boolean;
};

type ISize = {
  width: number;
  height: number;
};

const ProcedureActionImage = ({
  imageDataUrl,
  overlays,
  onImageClick,
  enableWheelZoom,
  hideZoomSlider = false
}: Props) => {
  const [zoom, setZoom] = useState(100);
  const [dimensions, setDimensions] = useState<IImageDimensions>({
    minimumWidth: 0,
    minimumHeight: 0,
    naturalWidth: 0,
    naturalHeight: 0,
    parentWidth: 0,
    parentHeight: 0
  });
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const [isMobile] = useState<boolean>(
    getService('browserUtilsService').isMobileDevice()
  );
  const minZoom = 100;
  const maxZoom = 200;

  const focusImageOnTarget = (
    img: HTMLDivElement,
    dimensions: IImageDimensions | null
  ) => {
    if (!img || !dimensions) return;
    const targetOverlay = overlays.at(0);
    if (!targetOverlay) return;

    const parent = img.parentElement;
    if (zoom === minZoom) {
      parent.scrollLeft = 0;
      parent.scrollTop = 0;
      return;
    }
    const overlayCenterX = targetOverlay?.x + targetOverlay?.width / 2;
    const overlayCenterY = targetOverlay?.y + targetOverlay?.height / 2;
    const mouseXPercent = targetOverlay
      ? overlayCenterX / dimensions.naturalWidth
      : dimensions.minimumWidth / 2;
    const mouseYPercent = targetOverlay
      ? overlayCenterY / dimensions.naturalHeight
      : dimensions.minimumHeight / 2;

    const zoomedX = img.clientWidth * mouseXPercent;
    const zoomedY = img.clientHeight * mouseYPercent;

    const scrollLeft = zoomedX - parent.clientWidth / 2;
    const scrollTop = zoomedY - parent.clientHeight / 2;

    const cappedScrollLeft = Math.min(
      scrollLeft,
      img.offsetWidth - parent.clientWidth
    );
    const cappedScrollTop = Math.min(
      scrollTop,
      img.offsetHeight - parent.clientHeight
    );
    parent.scrollLeft = Math.max(0, cappedScrollLeft);
    parent.scrollTop = Math.max(0, cappedScrollTop);
  };
  useEffect(() => {
    focusImageOnTarget(imageContainerRef.current, dimensions);
  }, [imageContainerRef.current, zoom, dimensions]);

  const updateZoomFromWheel = (event: WheelEvent) => {
    if (!enableWheelZoom) return;
    event.preventDefault();
    const delta = (maxZoom - minZoom) / 10;
    const zoomDelta = event.deltaY < 0 ? delta : -delta;
    const newZoom = Math.min(Math.max(zoom + zoomDelta, minZoom), maxZoom);
    setZoom(newZoom);
  };

  const prevZoomDivRef = React.useRef<HTMLDivElement | null>(null);
  const zoomDiv = useCallback(
    (node: HTMLDivElement | null): void => {
      if (prevZoomDivRef.current) {
        prevZoomDivRef.current.removeEventListener(
          'wheel',
          updateZoomFromWheel
        );
      }
      if (node) {
        node.addEventListener('wheel', updateZoomFromWheel, { passive: false });
      }

      prevZoomDivRef.current = node;
    },
    [updateZoomFromWheel]
  );

  useEffect(() => {
    return () => {
      if (prevZoomDivRef.current) {
        prevZoomDivRef.current.removeEventListener(
          'wheel',
          updateZoomFromWheel
        );
      }
    };
  }, []);

  const getContainedSize = (
    containerWidth: number,
    containerHeight: number,
    naturalWidth: number,
    naturalHeight: number
  ): ISize => {
    const imageRatio = naturalWidth / naturalHeight;

    if (!containerHeight) {
      return {
        width: containerWidth,
        height: containerWidth / imageRatio
      };
    }

    const containerRatio = containerWidth / containerHeight;
    if (imageRatio > containerRatio) {
      return {
        width: containerWidth,
        height: containerWidth / imageRatio
      };
    }

    return {
      width: containerHeight * imageRatio,
      height: containerHeight
    };
  };

  const getDisplayedImageSize = (img: HTMLImageElement): ISize => {
    return getContainedSize(
      img.parentElement.clientWidth,
      img.parentElement.clientHeight,
      img.naturalWidth,
      img.naturalHeight
    );
  };

  useEffect(() => {
    const anchor = anchorRef.current;
    const resizeTarget = anchor?.parentElement || anchor;
    if (
      !resizeTarget ||
      !dimensions.naturalWidth ||
      !dimensions.naturalHeight
    ) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const displayedSize = getContainedSize(
          width,
          height,
          dimensions.naturalWidth,
          dimensions.naturalHeight
        );

        setDimensions((prev) => {
          if (
            prev.minimumWidth === displayedSize.width &&
            prev.minimumHeight === displayedSize.height &&
            prev.parentWidth === width &&
            prev.parentHeight === height
          ) {
            return prev;
          }

          return {
            ...prev,
            minimumWidth: displayedSize.width,
            minimumHeight: displayedSize.height,
            parentWidth: width,
            parentHeight: height
          };
        });
      }
    });

    resizeObserver.observe(resizeTarget);
    return () => resizeObserver.disconnect();
  }, [dimensions.naturalWidth, dimensions.naturalHeight]);

  const isPortrait = dimensions.naturalHeight > dimensions.naturalWidth;
  const baseWidth = Math.ceil(dimensions.minimumWidth);
  const baseHeight = Math.ceil(dimensions.minimumHeight);
  const heightScale = isPortrait ? 0.9 : 1;

  const containerStyles: React.CSSProperties = dimensions.minimumWidth
    ? {
        overflow: zoom === minZoom ? 'clip' : 'auto',
        width: '100%',
        height: `${baseHeight}px`
      }
    : {};

  const scaleFactor = zoom / minZoom;
  const buildScaledSize = (scale: number): ISize => ({
    width: Math.ceil(baseWidth * scale),
    height: Math.ceil(baseHeight * scale)
  });
  const buildStyleFromSize = (size: ISize): React.CSSProperties => ({
    width: `${size.width}px`,
    maxWidth: 'none',
    height: `${size.height}px`,
    maxHeight: 'none'
  });
  const containerScaledSize = buildScaledSize(scaleFactor);
  const imageScaledSize = buildScaledSize(scaleFactor * heightScale);
  const actionImageContainerStyles: React.CSSProperties =
    dimensions.minimumWidth === 0
      ? {}
      : buildStyleFromSize(containerScaledSize);
  const imageStyles: React.CSSProperties =
    dimensions.minimumWidth === 0 ? {} : buildStyleFromSize(imageScaledSize);
  const overlayStyles: React.CSSProperties = {
    ...imageStyles,
    display: overlays.length > 0 ? 'block' : 'none'
  };

  const setContainerRefs = useCallback(
    (node: HTMLDivElement | null): void => {
      containerRef.current = node;
      zoomDiv(node);
    },
    [zoomDiv]
  );

  if (!imageDataUrl) {
    return <div className="procedure-action-image no-image"></div>;
  }

  return (
    <div className="procedure-action-image-zoom-anchor" ref={anchorRef}>
      <div
        className="procedure-action-image"
        onClick={onImageClick}
        style={containerStyles}
        ref={setContainerRefs}
      >
        {dimensions.minimumWidth === 0 && (
          <img
            src={imageDataUrl}
            onLoad={(event) => {
              const target = event.target as HTMLImageElement;
              const displayedSize = getDisplayedImageSize(target);
              setDimensions({
                minimumWidth: displayedSize.width,
                minimumHeight: displayedSize.height,
                naturalWidth: target.naturalWidth,
                naturalHeight: target.naturalHeight,
                parentWidth: target.parentElement.clientWidth,
                parentHeight: target.parentElement.clientHeight
              });
            }}
          ></img>
        )}
        {dimensions.minimumWidth !== 0 && (
          <div
            className="action-image-container"
            style={actionImageContainerStyles}
            ref={imageContainerRef}
          >
            <img src={imageDataUrl} style={imageStyles}></img>
            <div className="overlay-background" style={overlayStyles}>
              {overlays.map((overlay) => {
                const overlayLeftPercent =
                  (overlay.x / dimensions.naturalWidth) * 100;
                const overlayTopPercent =
                  (overlay.y / dimensions.naturalHeight) * 100;
                const overlayWidthPercent =
                  (overlay.width / dimensions.naturalWidth) * 100;
                const overlayHeightPercent =
                  (overlay.height / dimensions.naturalHeight) * 100;

                const placeOnLeft =
                  overlayLeftPercent >
                  100 - (overlayLeftPercent + overlayWidthPercent);

                return (
                  <div
                    key={overlay.id}
                    className="overlay"
                    style={{
                      left: `${overlayLeftPercent}%`,
                      top: `${overlayTopPercent}%`,
                      width: `${overlayWidthPercent}%`,
                      height: `${overlayHeightPercent}%`
                    }}
                  >
                    <div className="overlay-content"></div>
                    <div
                      className="highlight-arrow"
                      style={{
                        left: placeOnLeft ? `-62px` : `calc(100% + 10px)`
                      }}
                    >
                      {placeOnLeft ? (
                        <i className="fa-light fa-arrow-turn-down-right"></i>
                      ) : (
                        <i className="fa-light fa-arrow-turn-down-left"></i>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {!hideZoomSlider && (
        <div
          className={'zoom-slider' + (isMobile ? ' mobile' : '')}
          onClick={(e) => e.stopPropagation()}
          data-testid="procedure-action-image-zoom-slider"
        >
          <DraggableSliderContainer
            min={minZoom}
            max={maxZoom}
            value={zoom}
            onChange={setZoom}
          />
        </div>
      )}
    </div>
  );
};

export default ProcedureActionImage;

export interface IImageDimensions {
  minimumWidth: number;
  minimumHeight: number;
  naturalWidth: number;
  naturalHeight: number;
  parentWidth?: number;
  parentHeight?: number;
}
